import { Alert, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import React, { useCallback, useEffect, useState } from 'react';
import applicationDetailSignal from '../ApplicationDetail.signal';
import {
  fetchLoanEvents,
  fetchClaimEvents,
  fetchLoanTrackingOptions,
  createEvent,
  updateEvent,
  deleteEvent,
  fetchEdoUsers,
  fetchApplicationDetails,
  fetchLoanTrackingPdfPreviewUrl,
  saveLoanTrackingPdfDocument,
} from '../ApplicationDetail.helpers';
import { refreshListAndModal } from '../../Approvable/Approvable.helpers';
import loaderSignal from 'signals/Loader.signal';
import alertSignal from 'signals/Alert.signal';
import userAccountSignal from 'signals/UserAccount.signal';
import $appSettings from 'signals/AppSettings.signal';
import {
  ASSIGNED_TO_ADMIN,
  LOAN_TRACKING_EVENT_TYPE_LABELS,
  LOAN_TRACKING_EVENT_STATUS_LABELS,
  loanTrackingEventTypeDisplay,
  loanTrackingEventStatusDisplay,
} from '../../Constant/constants';
import { User } from 'types';
import ModalWrapper from 'components/global/ModalWrapper/ModalWrapper';
import CloseModalButton from 'components/global/Constant/CloseModalButton';
import ApplicationLoanBatchPanel from './ApplicationLoanBatchPanel';
import { APPLICATION_LOAN_TRACKING_REFRESH_EVENT } from 'libs/loanProcessingBatchesDashboardDirty';
import { LOAN_PROCESSING_BATCH_STATUS_LABELS } from 'libs/loanProcessingBatchStatusLabels';

/** Same calendar display as Loan / Claim tracking date cells (MM/DD/YYYY). */
const formatTrackingTableDate = (iso: string | null | undefined): React.ReactNode => {
  if (!iso) return <span className="text-muted">-</span>;
  const dateOnly = iso.includes('T') ? iso.split('T')[0] : iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return <span className="text-muted">-</span>;
  const [year, month, day] = dateOnly.split('-').map(Number);
  const formattedMonth = String(month).padStart(2, '0');
  const formattedDay = String(day).padStart(2, '0');
  return `${formattedMonth}/${formattedDay}/${year}`;
};

const getTodayLocalDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type EventRecord = {
  id: number;
  applicationId?: number | null;
  eventCode: string;
  status?: string;
  eventDate?: string;
  assignedUserId?: string;
  assignedUser?: User;
  note?: string;
  claimId?: number | null;
  createdDate: string;
  updatedDate: string;
  readOnly?: boolean;
  createdBy?: User;
  updatedBy?: User;
};

type LossClaimOption = { id: number; claId?: number | null };

const ApplicationEventsTab: React.FC = () => {
  const {
    id: applicationId,
    currentTab,
    lossClaims: lossClaimsFromSignal,
    status: applicationStatus,
  } = applicationDetailSignal.value;
  const { isContentLoading } = loaderSignal.value;
  const [events, setEvents] = useState<EventRecord[]>([]);
  const [lossClaims, setLossClaims] = useState<LossClaimOption[]>([]);
  const [edoUsers, setEdoUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState({
    eventCode: '',
    status: '',
    eventDate: getTodayLocalDate(),
    assignedUserId: '',
    note: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isAddingNewClaim, setIsAddingNewClaim] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formDataClaim, setFormDataClaim] = useState({
    eventCode: '',
    status: '',
    eventDate: getTodayLocalDate(),
    assignedUserId: '',
    claimId: '',
    note: '',
  });
  const [formErrorsClaim, setFormErrorsClaim] = useState<Record<string, boolean>>({});
  const [editFormData, setEditFormData] = useState<{
    eventCode: string;
    status: string;
    eventDate: string;
    assignedUserId: string;
    claimId: string;
    note: string;
  } | null>(null);
  const [editFormErrors, setEditFormErrors] = useState<Record<string, boolean>>({});
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isSavingPdf, setIsSavingPdf] = useState(false);
  const [loanTrackingOptions, setLoanTrackingOptions] = useState<{
    allowedEventTypes: string[];
    allowedEventStatuses: string[];
    canAdd: boolean;
    canEdit: boolean;
    canRemove: boolean;
    processingBatchMemberships: Array<{
      memberId: number;
      addedToBatchAt: string;
      batch: {
        id: number;
        name: string;
        status: string;
        fundingProgramName: string | null;
      };
    }>;
  }>({
    allowedEventTypes: [],
    allowedEventStatuses: [],
    canAdd: false,
    canEdit: false,
    canRemove: false,
    processingBatchMemberships: [],
  });

  const loadData = useCallback(async (opts?: { silent?: boolean }) => {
    if (!applicationId) return;
    const silent = opts?.silent === true;

    try {
      if (!silent) {
        loaderSignal.update({ isContentLoading: true });
      }

      const users = await fetchEdoUsers();
      const usersArray = Array.isArray(users) ? users : [];
      setEdoUsers(usersArray);

      const [options, details] = await Promise.all([
        fetchLoanTrackingOptions(applicationId),
        fetchApplicationDetails(applicationId).catch(() => null),
      ]);
      setLoanTrackingOptions(options);
      const batchMemberships = Array.isArray(options.processingBatchMemberships)
        ? options.processingBatchMemberships
        : [];
      applicationDetailSignal.update({ processingBatchMemberships: batchMemberships });
      const claims = Array.isArray(details?.lossClaims) ? details.lossClaims : [];
      setLossClaims(claims.map((c: { id: number; claId?: number | null }) => ({ id: c.id, claId: c.claId })));
      const claimIds = claims.map((c: { id: number }) => c.id);

      const [loanEventRecords, claimEventRecords] = await Promise.all([
        fetchLoanEvents(applicationId).catch((err) => {
          console.error('Error fetching loan events (non-fatal):', err);
          return [];
        }),
        fetchClaimEvents(claimIds).catch((err) => {
          console.error('Error fetching claim events (non-fatal):', err);
          return [];
        }),
      ]);
      setEvents([...(loanEventRecords || []), ...(claimEventRecords || [])]);
    } catch (error: any) {
      console.error('Error loading events data:', error);
      alertSignal.update({
        message: error.message || 'Failed to load events data',
        variant: 'danger',
        type: 'notification',
      });
    } finally {
      if (!silent) {
        loaderSignal.update({ isContentLoading: false });
      }
    }
  }, [applicationId]);

  useEffect(() => {
    if (applicationId && currentTab === 'loanTracking') {
      loadData();
    }
  }, [applicationId, currentTab, loadData]);

  useEffect(() => {
    if (currentTab !== 'loanTracking') return;

    const onLoanTrackingExternalRefresh = (e: Event) => {
      const { id, currentTab: tab } = applicationDetailSignal.value;
      if (!id || tab !== 'loanTracking') return;
      const ce = e as CustomEvent<{ applicationId?: number; applicationIds?: number[] }>;
      const { applicationId: oneId, applicationIds } = ce.detail || {};
      if (applicationIds?.length) {
        if (!applicationIds.includes(id)) return;
      } else if (oneId != null && oneId !== id) {
        return;
      }
      void loadData({ silent: true });
    };

    window.addEventListener(APPLICATION_LOAN_TRACKING_REFRESH_EVENT, onLoanTrackingExternalRefresh);
    return () => window.removeEventListener(APPLICATION_LOAN_TRACKING_REFRESH_EVENT, onLoanTrackingExternalRefresh);
  }, [currentTab, loadData]);

  useEffect(() => {
    if (applicationStatus !== $appSettings.value.constants.APPLICATION_STATUS.inProgress) return;
    setIsAddingNew(false);
    setIsAddingNewClaim(false);
    setEditingId(null);
  }, [applicationStatus]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const updates: Partial<typeof formData> = { [name]: value };
    if (name === 'eventCode' && value === 'SA') {
      updates.assignedUserId = ASSIGNED_TO_ADMIN;
    } else if (name === 'eventCode' && formData.assignedUserId === ASSIGNED_TO_ADMIN) {
      updates.assignedUserId = '';
    }
    setFormData((prev) => ({ ...prev, ...updates }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, eventDate: e.target.value }));
    if (formErrors.eventDate) {
      setFormErrors((prev) => ({ ...prev, eventDate: false }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, boolean> = {};
    
    if (!formData.eventCode.trim()) {
      errors.eventCode = true;
    }

    if (!formData.status.trim()) {
      errors.status = true;
    }

    if (!formData.eventDate.trim()) {
      errors.eventDate = true;
    }

    if (!formData.assignedUserId.trim()) {
      errors.assignedUserId = true;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !applicationId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const newRecord = await createEvent({
        applicationId,
        eventCode: formData.eventCode,
        status: formData.status,
        eventDate: formData.eventDate,
        assignedUserId: formData.assignedUserId,
        note: formData.note || undefined,
      });

      setEvents((prev) => [newRecord, ...prev]);
      setFormData({
        eventCode: '',
        status: '',
        eventDate: getTodayLocalDate(),
        assignedUserId: '',
        note: '',
      });
      setFormErrors({});
      setIsAddingNew(false);

      if (applicationId) {
        const details = await fetchApplicationDetails(applicationId).catch(() => null);
        if (details) {
          applicationDetailSignal.update({
            calcapLoanNumber: details.calcapLoanNumber ?? null,
            lenderLoanNumber: details.lenderLoanNumber ?? null,
          });
        }
      }

      alertSignal.update({
        message: 'Event record created successfully',
        variant: 'success',
        type: 'notification',
      });
    } catch (error: any) {
      alertSignal.update({
        message: error.message || 'Failed to create event record',
        variant: 'danger',
        type: 'notification',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      eventCode: '',
      status: '',
      eventDate: getTodayLocalDate(),
      assignedUserId: '',
      note: '',
    });
    setFormErrors({});
    setIsAddingNew(false);
  };

  const handleInputChangeClaim = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const updates: Partial<typeof formDataClaim> = { [name]: value };
    if (name === 'eventCode' && value === 'SA') {
      updates.assignedUserId = ASSIGNED_TO_ADMIN;
    } else if (name === 'eventCode' && formDataClaim.assignedUserId === ASSIGNED_TO_ADMIN) {
      updates.assignedUserId = '';
    }
    setFormDataClaim((prev) => ({ ...prev, ...updates }));
    if (formErrorsClaim[name]) {
      setFormErrorsClaim((prev) => ({ ...prev, [name]: false }));
    }
  };

  const validateFormClaim = (): boolean => {
    const errors: Record<string, boolean> = {};
    if (!formDataClaim.eventCode.trim()) errors.eventCode = true;
    if (!formDataClaim.status.trim()) errors.status = true;
    if (!formDataClaim.eventDate.trim()) errors.eventDate = true;
    if (!formDataClaim.assignedUserId.trim()) errors.assignedUserId = true;
    if (!formDataClaim.claimId.trim()) errors.claimId = true;
    setFormErrorsClaim(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateFormClaim() || !applicationId) return;

    setIsSubmitting(true);
    try {
      const claimIdNum = parseInt(formDataClaim.claimId, 10);
      const newRecord = await createEvent({
        eventCode: formDataClaim.eventCode,
        status: formDataClaim.status,
        eventDate: formDataClaim.eventDate,
        assignedUserId: formDataClaim.assignedUserId,
        note: formDataClaim.note || undefined,
        claimId: claimIdNum,
      });

      setEvents((prev) => [newRecord, ...prev]);
      setFormDataClaim({
        eventCode: '',
        status: '',
        eventDate: getTodayLocalDate(),
        assignedUserId: '',
        claimId: '',
        note: '',
      });
      setFormErrorsClaim({});
      setIsAddingNewClaim(false);

      alertSignal.update({
        message: 'Event record created successfully',
        variant: 'success',
        type: 'notification',
      });
    } catch (error: any) {
      alertSignal.update({
        message: error.message || 'Failed to create event record',
        variant: 'danger',
        type: 'notification',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelClaim = () => {
    setFormDataClaim({
      eventCode: '',
      status: '',
      eventDate: getTodayLocalDate(),
      assignedUserId: '',
      claimId: '',
      note: '',
    });
    setFormErrorsClaim({});
    setIsAddingNewClaim(false);
  };

  const handleEdit = (record: EventRecord) => {
    const dateOnly = record.eventDate && record.eventDate.includes('T')
      ? record.eventDate.split('T')[0]
      : record.eventDate || getTodayLocalDate();

    const assignedUserId =
      record.eventCode === 'SA' && !record.assignedUser?.id
        ? ASSIGNED_TO_ADMIN
        : record.assignedUserId || record.assignedUser?.id || '';

    const eventCode = loanTrackingOptions.allowedEventTypes.includes(record.eventCode)
      ? record.eventCode
      : loanTrackingOptions.allowedEventTypes[0] || '';
    const status = loanTrackingOptions.allowedEventStatuses.includes(record.status || '')
      ? (record.status || '')
      : loanTrackingOptions.allowedEventStatuses[0] || '';

    setEditingId(record.id);
    setEditFormData({
      eventCode,
      status,
      eventDate: dateOnly,
      assignedUserId: assignedUserId,
      claimId: record.claimId != null ? String(record.claimId) : '',
      note: record.note || '',
    });
    setEditFormErrors({});
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    if (!editFormData) return;
    const { name, value } = e.target;
    const updates: Partial<typeof editFormData> = { [name]: value };
    if (name === 'eventCode' && value === 'SA') {
      updates.assignedUserId = ASSIGNED_TO_ADMIN;
    } else if (name === 'eventCode' && editFormData.assignedUserId === ASSIGNED_TO_ADMIN) {
      updates.assignedUserId = '';
    }
    setEditFormData({ ...editFormData, ...updates });
    if (editFormErrors[name]) {
      setEditFormErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleEditDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editFormData) return;
    setEditFormData({ ...editFormData, eventDate: e.target.value });
    if (editFormErrors.eventDate) {
      setEditFormErrors((prev) => ({ ...prev, eventDate: false }));
    }
  };

  const validateEditForm = (record: EventRecord): boolean => {
    if (!editFormData) return false;
    const errors: Record<string, boolean> = {};

    if (!editFormData.eventCode.trim()) {
      errors.eventCode = true;
    }

    if (!editFormData.status.trim()) {
      errors.status = true;
    }

    if (!editFormData.eventDate.trim()) {
      errors.eventDate = true;
    }

    if (!editFormData.assignedUserId.trim()) {
      errors.assignedUserId = true;
    }

    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveEdit = async (record: EventRecord) => {
    if (!editFormData || !validateEditForm(record)) {
      return;
    }

    const updatePayload: Parameters<typeof updateEvent>[1] = {
      eventCode: editFormData.eventCode,
      status: editFormData.status,
      eventDate: editFormData.eventDate,
      assignedUserId: editFormData.assignedUserId,
      note: editFormData.note || undefined,
    };
    if (record.claimId != null) {
      if (editFormData.claimId.trim()) {
        updatePayload.claimId = parseInt(editFormData.claimId, 10);
        updatePayload.applicationId = null;
      } else {
        updatePayload.claimId = null;
        updatePayload.applicationId = applicationId ?? null;
      }
    }

    setIsSubmitting(true);
    try {
      const updatedRecord = await updateEvent(record.id, updatePayload);

      setEvents((prev) =>
        prev.map((r) => (r.id === record.id ? updatedRecord : r))
      );

      setEditingId(null);
      setEditFormData(null);
      setEditFormErrors({});

      if (record.applicationId && applicationId) {
        const details = await fetchApplicationDetails(applicationId).catch(() => null);
        if (details) {
          applicationDetailSignal.update({
            calcapLoanNumber: details.calcapLoanNumber ?? null,
            lenderLoanNumber: details.lenderLoanNumber ?? null,
          });
        }
      }

      alertSignal.update({
        message: 'Event record updated successfully',
        variant: 'success',
        type: 'notification',
      });
    } catch (error: any) {
      alertSignal.update({
        message: error.message || 'Failed to update event record',
        variant: 'danger',
        type: 'notification',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData(null);
    setEditFormErrors({});
  };

  const handleDelete = async (recordId: number) => {
    if (!window.confirm('Are you sure you want to delete this record? This action cannot be undone.')) {
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteEvent(recordId);
      setEvents((prev) => prev.filter((record) => record.id !== recordId));

      alertSignal.update({
        message: 'Event record deleted successfully',
        variant: 'success',
        type: 'notification',
      });
    } catch (error: any) {
      alertSignal.update({
        message: error.message || 'Failed to delete event record',
        variant: 'danger',
        type: 'notification',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUserDisplayName = (user?: User | { displayName?: string }) => {
    if (!user) return 'N/A';
    const displayName = (user as { displayName?: string }).displayName;
    if (displayName) return displayName;
    const name = `${(user as User).firstName} ${(user as User).lastName}`.trim();
    return name || (user as User).email;
  };

  const { PORTAL_TYPES, APPLICATION_STATUS } = $appSettings.value.constants;
  const isEdoUser = userAccountSignal?.value?.userData?.account?.portalType === PORTAL_TYPES?.edo;
  const isLender = userAccountSignal?.value?.userData?.account?.portalType === PORTAL_TYPES?.lender;
  const loanTrackingDisabled = applicationStatus === APPLICATION_STATUS.inProgress;

  const loanEvents = events.filter((e) => e.claimId == null);
  const claimEvents = events.filter((e) => e.applicationId == null && e.claimId != null);

  const claimsFromSignal = Array.isArray(lossClaimsFromSignal) ? lossClaimsFromSignal : [];
  const lossClaimsToUse =
    claimsFromSignal.length >= lossClaims.length
      ? claimsFromSignal.map((c: { id: number; claId?: number | null }) => ({ id: c.id, claId: c.claId }))
      : lossClaims;

  const getClaimDisplayId = (claimId: number): string => {
    const c = lossClaimsToUse.find((lc) => lc.id === claimId);
    return c?.claId != null ? String(c.claId) : String(claimId);
  };

  const handleGeneratePdfClick = async () => {
    if (!applicationId) return;
    setIsGeneratingPdf(true);
    try {
      const url = await fetchLoanTrackingPdfPreviewUrl(applicationId);
      setPdfPreviewUrl(url);
      setShowPdfModal(true);
    } catch (error: any) {
      alertSignal.update({
        message: error?.message || 'Failed to generate PDF preview',
        variant: 'danger',
        type: 'notification',
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleClosePdfModal = () => {
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
    }
    setPdfPreviewUrl(null);
    setShowPdfModal(false);
  };

  const handleSavePdfToDocuments = async () => {
    if (!applicationId) return;
    setIsSavingPdf(true);
    try {
      await saveLoanTrackingPdfDocument(applicationId);
      const { id, modelType, ventureCapitalist } = applicationDetailSignal.value;
      if (id != null) {
        await refreshListAndModal(id, modelType ?? 'application', ventureCapitalist ?? false);
      }
      alertSignal.update({
        message: 'Loan Tracking PDF saved to Documents.',
        variant: 'success',
        type: 'notification',
      });
      handleClosePdfModal();
    } catch (error: any) {
      alertSignal.update({
        message: error?.message || 'Failed to save PDF to Documents',
        variant: 'danger',
        type: 'notification',
      });
    } finally {
      setIsSavingPdf(false);
    }
  };

  return (
    <div>
      {loanTrackingDisabled && (
        <Alert variant="info" className="mb-16">
          Loan tracking, PDF export, and batch assignment are not available while this application is a draft (in progress).
        </Alert>
      )}
      <ApplicationLoanBatchPanel />

      {loanTrackingOptions.processingBatchMemberships.length > 0 && (
        <>
          <Row className="mb-16">
            <Col>
              <h5 className="mb-0">Loan Processing Batches</h5>
            </Col>
          </Row>
          <Table striped bordered hover responsive className="align-top table-events mb-32">
            <thead>
              <tr>
                <th>Batch name</th>
                <th>Program</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {loanTrackingOptions.processingBatchMemberships.map((m) => (
                <tr key={m.memberId}>
                  <td>{m.batch.name || <span className="text-muted">-</span>}</td>
                  <td>
                    {m.batch.fundingProgramName ? (
                      m.batch.fundingProgramName
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {m.batch.status ? (
                      LOAN_PROCESSING_BATCH_STATUS_LABELS[m.batch.status] ?? m.batch.status
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>{formatTrackingTableDate(m.addedToBatchAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      <Row className="mb-16">
        <Col>
          <h5>Loan Tracking</h5>
        </Col>
        <Col className="text-right">
          {isEdoUser && (
            <Button
              variant="outline-primary"
              className="rounded mr-2"
              onClick={handleGeneratePdfClick}
              disabled={
                loanTrackingDisabled ||
                isGeneratingPdf ||
                isAddingNew ||
                isAddingNewClaim ||
                editingId !== null
              }
            >
              {isGeneratingPdf ? 'Generating…' : 'Generate PDF'}
            </Button>
          )}
          {!isLender &&
            !loanTrackingDisabled &&
            loanTrackingOptions.canAdd &&
            loanTrackingOptions.allowedEventTypes.length > 0 && (
            <Button
              variant="primary"
              className="rounded"
              onClick={() => setIsAddingNew(true)}
              disabled={isAddingNew || isAddingNewClaim || editingId !== null}
            >
              Add New Record
            </Button>
          )}
        </Col>
      </Row>

      <Table striped bordered hover responsive className="align-top table-events">
        <thead>
          <tr>
            <th>Event Type</th>
            <th>Status</th>
            <th>Date</th>
            <th>Responsible Party</th>
            <th className="absorbing-column">Event Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isAddingNew && !isLender && !loanTrackingDisabled && (
            <tr className="bg-light" style={{ verticalAlign: 'top' }}>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <Form.Select
                  className="form-control"
                  name="eventCode"
                  value={formData.eventCode}
                  onChange={handleInputChange}
                  isInvalid={formErrors.eventCode}
                  size="sm"
                  required
                >
                  <option value="">Select</option>
                  {loanTrackingOptions.allowedEventTypes.map((code) => (
                    <option key={code} value={code}>
                      {(LOAN_TRACKING_EVENT_TYPE_LABELS as Record<string, string>)[code] || code}
                    </option>
                  ))}
                </Form.Select>
                {formErrors.eventCode && (
                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                    Required
                  </Form.Text>
                )}
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <Form.Select
                  className="form-control"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  isInvalid={formErrors.status}
                  size="sm"
                  required
                >
                  <option value="">Select</option>
                  {loanTrackingOptions.allowedEventStatuses.map((code) => (
                    <option key={code} value={code}>
                      {(LOAN_TRACKING_EVENT_STATUS_LABELS as Record<string, string>)[code] || code}
                    </option>
                  ))}
                </Form.Select>
                {formErrors.status && (
                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                    Required
                  </Form.Text>
                )}
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <Form.Control
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleDateChange}
                  isInvalid={formErrors.eventDate}
                  size="sm"
                  className="form-control"
                  required
                  style={{ height: '38px', fontSize: 'inherit' }}
                />
                {formErrors.eventDate && (
                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                    Required
                  </Form.Text>
                )}
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <Form.Select
                  className="form-control"
                  name="assignedUserId"
                  value={formData.assignedUserId}
                  onChange={handleInputChange}
                  isInvalid={formErrors.assignedUserId}
                  size="sm"
                  disabled={
                    formData.eventCode !== 'SA' &&
                    edoUsers.length === 0
                  }
                  required
                >
                  <option value="">
                    {formData.eventCode !== 'SA' && edoUsers.length === 0
                      ? 'No users available'
                      : 'Select'}
                  </option>
                  {loanTrackingOptions.allowedEventTypes.includes('SA') && (
                    <option value={ASSIGNED_TO_ADMIN}>Admin</option>
                  )}
                  {edoUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {getUserDisplayName(user)}
                    </option>
                  ))}
                </Form.Select>
                {formErrors.assignedUserId && (
                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                    Required
                  </Form.Text>
                )}
              </td>
              <td className="absorbing-column">
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  placeholder="Enter event notes..."
                  size="sm"
                  style={{ fontSize: 'inherit' }}
                />
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <span style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    type="button"
                    variant="success"
                    className="rounded"
                    size="sm"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isContentLoading}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="rounded"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </span>
              </td>
            </tr>
          )}

          {loanEvents.length === 0 && !isAddingNew ? (
            <tr>
              <td colSpan={6} className="text-center text-muted py-4">
                {isLender ? 'No events yet.' : 'No events yet. Click "Add New Record" to create one.'}
              </td>
            </tr>
          ) : (
            loanEvents.map((record) => {
              const isEditing = editingId === record.id;
              
              if (isEditing && editFormData) {
                return (
                  <tr key={record.id} className="bg-light" style={{ verticalAlign: 'top' }}>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <Form.Select
                        className="form-control"
                        name="eventCode"
                        value={editFormData.eventCode}
                        onChange={handleEditInputChange}
                        isInvalid={editFormErrors.eventCode}
                        size="sm"
                        required
                      >
                        <option value="">Select</option>
                        {loanTrackingOptions.allowedEventTypes.map((code) => (
                          <option key={code} value={code}>
                            {(LOAN_TRACKING_EVENT_TYPE_LABELS as Record<string, string>)[code] || code}
                          </option>
                        ))}
                      </Form.Select>
                      {editFormErrors.eventCode && (
                        <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                          Required
                        </Form.Text>
                      )}
                    </td>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <Form.Select
                        className="form-control"
                        name="status"
                        value={editFormData.status}
                        onChange={handleEditInputChange}
                        isInvalid={editFormErrors.status}
                        size="sm"
                        required
                      >
                        <option value="">Select</option>
                        {loanTrackingOptions.allowedEventStatuses.map((code) => (
                          <option key={code} value={code}>
                            {(LOAN_TRACKING_EVENT_STATUS_LABELS as Record<string, string>)[code] || code}
                          </option>
                        ))}
                      </Form.Select>
                      {editFormErrors.status && (
                        <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                          Required
                        </Form.Text>
                      )}
                    </td>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <Form.Control
                        type="date"
                        name="eventDate"
                        value={editFormData.eventDate}
                        onChange={handleEditDateChange}
                        isInvalid={editFormErrors.eventDate}
                        size="sm"
                        className="form-control"
                        required
                        style={{ height: '38px', fontSize: 'inherit' }}
                      />
                      {editFormErrors.eventDate && (
                        <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                          Required
                        </Form.Text>
                      )}
                    </td>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <Form.Select
                        className="form-control"
                        name="assignedUserId"
                        value={editFormData.assignedUserId}
                        onChange={handleEditInputChange}
                        isInvalid={editFormErrors.assignedUserId}
                        size="sm"
                        disabled={
                          editFormData.eventCode !== 'SA' &&
                          edoUsers.length === 0
                        }
                        required
                      >
                        <option value="">
                          {editFormData.eventCode !== 'SA' && edoUsers.length === 0
                            ? 'No users available'
                            : 'Select'}
                        </option>
                        {loanTrackingOptions.allowedEventTypes.includes('SA') && (
                          <option value={ASSIGNED_TO_ADMIN}>Admin</option>
                        )}
                        {edoUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {getUserDisplayName(user)}
                          </option>
                        ))}
                      </Form.Select>
                      {editFormErrors.assignedUserId && (
                        <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                          Required
                        </Form.Text>
                      )}
                    </td>
                    <td className="absorbing-column">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="note"
                        value={editFormData.note}
                        onChange={handleEditInputChange}
                        placeholder="Enter event notes..."
                        size="sm"
                        style={{ fontSize: 'inherit' }}
                      />
                    </td>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <span style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          type="button"
                          variant="success"
                          className="rounded"
                          size="sm"
                          onClick={() => handleSaveEdit(record)}
                          disabled={isSubmitting || isContentLoading}
                        >
                          {isSubmitting ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          className="rounded"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                      </span>
                    </td>
                  </tr>
                );
              }
              
              return (
                <tr key={record.id}>
                  <td>
                    {record.eventCode ? (
                      loanTrackingEventTypeDisplay(record.eventCode, record.status)
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {record.status
                      ? loanTrackingEventStatusDisplay(record.eventCode, record.status)
                      : <span className="text-muted">-</span>}
                  </td>
                  <td>
                    {record.eventDate 
                      ? (() => {
                          const dateOnly = record.eventDate.includes('T') 
                            ? record.eventDate.split('T')[0] 
                            : record.eventDate;
                          const [year, month, day] = dateOnly.split('-').map(Number);
                          const formattedMonth = String(month).padStart(2, '0');
                          const formattedDay = String(day).padStart(2, '0');
                          return `${formattedMonth}/${formattedDay}/${year}`;
                        })()
                      : <span className="text-muted">-</span>}
                  </td>
                  <td>
                    {record.assignedUser
                      ? getUserDisplayName(record.assignedUser)
                      : record.eventCode === 'SA'
                        ? 'Admin'
                        : <span className="text-muted">-</span>}
                  </td>
                  <td className="absorbing-column">{record.note || <span className="text-muted">-</span>}</td>
                  <td>
                    {isLender ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <span style={{ display: 'flex', gap: '8px' }}>
                        {!record.readOnly &&
                          loanTrackingOptions.canEdit &&
                          !loanTrackingDisabled && (
                          <Button
                            variant="outline-primary"
                            className="rounded"
                            size="sm"
                            onClick={() => handleEdit(record)}
                            disabled={isAddingNew || isAddingNewClaim || editingId !== null}
                          >
                            Edit
                          </Button>
                        )}
                        {!record.readOnly &&
                          loanTrackingOptions.canRemove &&
                          !loanTrackingDisabled && (
                          <Button
                            variant="outline-danger"
                            className="rounded"
                            size="sm"
                            onClick={() => handleDelete(record.id)}
                            disabled={isAddingNew || isAddingNewClaim || editingId !== null || isSubmitting}
                            title="Delete this record"
                          >
                            Delete
                          </Button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>

      <Row className="mb-3 mt-5">
        <Col>
          <h5>Claim Tracking</h5>
        </Col>
        <Col className="text-right">
          {!isLender &&
            !loanTrackingDisabled &&
            loanTrackingOptions.canAdd &&
            loanTrackingOptions.allowedEventTypes.length > 0 &&
            lossClaimsToUse.length > 0 && (
              <Button
                variant="primary"
                className="rounded"
                onClick={() => setIsAddingNewClaim(true)}
                disabled={isAddingNew || isAddingNewClaim || editingId !== null}
              >
                Add New Record
              </Button>
            )}
        </Col>
      </Row>

      <Table striped bordered hover responsive className="align-top table-events">
        <thead>
          <tr>
            <th>Event Type</th>
            <th>Status</th>
            <th>Date</th>
            <th>Claim ID</th>
            <th>Responsible Party</th>
            <th className="absorbing-column">Event Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isAddingNewClaim && !isLender && !loanTrackingDisabled && (
            <tr className="bg-light" style={{ verticalAlign: 'top' }}>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <Form.Select
                  className="form-control"
                  name="eventCode"
                  value={formDataClaim.eventCode}
                  onChange={handleInputChangeClaim}
                  isInvalid={formErrorsClaim.eventCode}
                  size="sm"
                  required
                >
                  <option value="">Select</option>
                  {loanTrackingOptions.allowedEventTypes.map((code) => (
                    <option key={code} value={code}>
                      {(LOAN_TRACKING_EVENT_TYPE_LABELS as Record<string, string>)[code] || code}
                    </option>
                  ))}
                </Form.Select>
                {formErrorsClaim.eventCode && (
                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                    Required
                  </Form.Text>
                )}
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <Form.Select
                  className="form-control"
                  name="status"
                  value={formDataClaim.status}
                  onChange={handleInputChangeClaim}
                  isInvalid={formErrorsClaim.status}
                  size="sm"
                  required
                >
                  <option value="">Select</option>
                  {loanTrackingOptions.allowedEventStatuses.map((code) => (
                    <option key={code} value={code}>
                      {(LOAN_TRACKING_EVENT_STATUS_LABELS as Record<string, string>)[code] || code}
                    </option>
                  ))}
                </Form.Select>
                {formErrorsClaim.status && (
                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                    Required
                  </Form.Text>
                )}
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <Form.Control
                  type="date"
                  name="eventDate"
                  value={formDataClaim.eventDate}
                  onChange={(e) => {
                    setFormDataClaim((prev) => ({ ...prev, eventDate: e.target.value }));
                    if (formErrorsClaim.eventDate) setFormErrorsClaim((prev) => ({ ...prev, eventDate: false }));
                  }}
                  isInvalid={formErrorsClaim.eventDate}
                  size="sm"
                  className="form-control"
                  required
                  style={{ height: '38px', fontSize: 'inherit' }}
                />
                {formErrorsClaim.eventDate && (
                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                    Required
                  </Form.Text>
                )}
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <Form.Select
                  className="form-control"
                  name="claimId"
                  value={formDataClaim.claimId}
                  onChange={handleInputChangeClaim}
                  isInvalid={formErrorsClaim.claimId}
                  size="sm"
                  required
                >
                  <option value="">Select</option>
                  {lossClaimsToUse.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.claId != null ? c.claId : `Claim ${c.id}`}
                    </option>
                  ))}
                </Form.Select>
                {formErrorsClaim.claimId && (
                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                    Required
                  </Form.Text>
                )}
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <Form.Select
                  className="form-control"
                  name="assignedUserId"
                  value={formDataClaim.assignedUserId}
                  onChange={handleInputChangeClaim}
                  isInvalid={formErrorsClaim.assignedUserId}
                  size="sm"
                  disabled={
                    formDataClaim.eventCode !== 'SA' && edoUsers.length === 0
                  }
                  required
                >
                  <option value="">
                    {formDataClaim.eventCode !== 'SA' && edoUsers.length === 0
                      ? 'No users available'
                      : 'Select'}
                  </option>
                  {loanTrackingOptions.allowedEventTypes.includes('SA') && (
                    <option value={ASSIGNED_TO_ADMIN}>Admin</option>
                  )}
                  {edoUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {getUserDisplayName(user)}
                    </option>
                  ))}
                </Form.Select>
                {formErrorsClaim.assignedUserId && (
                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                    Required
                  </Form.Text>
                )}
              </td>
              <td className="absorbing-column">
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="note"
                  value={formDataClaim.note}
                  onChange={handleInputChangeClaim}
                  placeholder="Enter event notes..."
                  size="sm"
                  style={{ fontSize: 'inherit' }}
                />
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                <span style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    type="button"
                    variant="success"
                    className="rounded"
                    size="sm"
                    onClick={(e) => handleSubmitClaim(e)}
                    disabled={isSubmitting || isContentLoading}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="rounded"
                    size="sm"
                    onClick={handleCancelClaim}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </span>
              </td>
            </tr>
          )}

          {claimEvents.length === 0 && !isAddingNewClaim ? (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                {lossClaimsToUse.length === 0
                  ? 'No loss claims for this application. Add claims in the Claims tab first.'
                  : isLender
                    ? 'No claim events yet.'
                    : 'No claim events yet. Click "Add New Record" to create one.'}
              </td>
            </tr>
          ) : (
            claimEvents.map((record) => {
              const isEditing = editingId === record.id;

              if (isEditing && editFormData) {
                return (
                  <tr key={record.id} className="bg-light" style={{ verticalAlign: 'top' }}>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <Form.Select
                        className="form-control"
                        name="eventCode"
                        value={editFormData.eventCode}
                        onChange={handleEditInputChange}
                        isInvalid={editFormErrors.eventCode}
                        size="sm"
                        required
                      >
                        <option value="">Select</option>
                        {loanTrackingOptions.allowedEventTypes.map((code) => (
                          <option key={code} value={code}>
                            {(LOAN_TRACKING_EVENT_TYPE_LABELS as Record<string, string>)[code] || code}
                          </option>
                        ))}
                      </Form.Select>
                      {editFormErrors.eventCode && (
                        <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                          Required
                        </Form.Text>
                      )}
                    </td>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <Form.Select
                        className="form-control"
                        name="status"
                        value={editFormData.status}
                        onChange={handleEditInputChange}
                        isInvalid={editFormErrors.status}
                        size="sm"
                        required
                      >
                        <option value="">Select</option>
                        {loanTrackingOptions.allowedEventStatuses.map((code) => (
                          <option key={code} value={code}>
                            {(LOAN_TRACKING_EVENT_STATUS_LABELS as Record<string, string>)[code] || code}
                          </option>
                        ))}
                      </Form.Select>
                      {editFormErrors.status && (
                        <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                          Required
                        </Form.Text>
                      )}
                    </td>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <Form.Control
                        type="date"
                        name="eventDate"
                        value={editFormData.eventDate}
                        onChange={handleEditDateChange}
                        isInvalid={editFormErrors.eventDate}
                        size="sm"
                        className="form-control"
                        required
                        style={{ height: '38px', fontSize: 'inherit' }}
                      />
                      {editFormErrors.eventDate && (
                        <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                          Required
                        </Form.Text>
                      )}
                    </td>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <Form.Select
                        className="form-control"
                        name="claimId"
                        value={editFormData.claimId}
                        onChange={handleEditInputChange}
                        size="sm"
                      >
                        <option value="">— None (move to Loan Tracking)</option>
                        {lossClaimsToUse.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.claId != null ? c.claId : `Claim ${c.id}`}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <Form.Select
                        className="form-control"
                        name="assignedUserId"
                        value={editFormData.assignedUserId}
                        onChange={handleEditInputChange}
                        isInvalid={editFormErrors.assignedUserId}
                        size="sm"
                        disabled={
                          editFormData.eventCode !== 'SA' && edoUsers.length === 0
                        }
                        required
                      >
                        <option value="">
                          {editFormData.eventCode !== 'SA' && edoUsers.length === 0
                            ? 'No users available'
                            : 'Select'}
                        </option>
                        {loanTrackingOptions.allowedEventTypes.includes('SA') && (
                          <option value={ASSIGNED_TO_ADMIN}>Admin</option>
                        )}
                        {edoUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {getUserDisplayName(user)}
                          </option>
                        ))}
                      </Form.Select>
                      {editFormErrors.assignedUserId && (
                        <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>
                          Required
                        </Form.Text>
                      )}
                    </td>
                    <td className="absorbing-column">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="note"
                        value={editFormData.note}
                        onChange={handleEditInputChange}
                        placeholder="Enter event notes..."
                        size="sm"
                        style={{ fontSize: 'inherit' }}
                      />
                    </td>
                    <td style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                      <span style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          type="button"
                          variant="success"
                          className="rounded"
                          size="sm"
                          onClick={() => handleSaveEdit(record)}
                          disabled={isSubmitting || isContentLoading}
                        >
                          {isSubmitting ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          className="rounded"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                      </span>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={record.id}>
                  <td>
                    {record.eventCode ? (
                      loanTrackingEventTypeDisplay(record.eventCode, record.status)
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {record.status
                      ? loanTrackingEventStatusDisplay(record.eventCode, record.status)
                      : <span className="text-muted">-</span>}
                  </td>
                  <td>
                    {record.eventDate
                      ? (() => {
                          const dateOnly = record.eventDate.includes('T')
                            ? record.eventDate.split('T')[0]
                            : record.eventDate;
                          const [year, month, day] = dateOnly.split('-').map(Number);
                          const formattedMonth = String(month).padStart(2, '0');
                          const formattedDay = String(day).padStart(2, '0');
                          return `${formattedMonth}/${formattedDay}/${year}`;
                        })()
                      : <span className="text-muted">-</span>}
                  </td>
                  <td>{record.claimId != null ? getClaimDisplayId(record.claimId) : <span className="text-muted">-</span>}</td>
                  <td>
                    {record.assignedUser
                      ? getUserDisplayName(record.assignedUser)
                      : record.eventCode === 'SA'
                        ? 'Admin'
                        : <span className="text-muted">-</span>}
                  </td>
                  <td className="absorbing-column">{record.note || <span className="text-muted">-</span>}</td>
                  <td>
                    {isLender ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <span style={{ display: 'flex', gap: '8px' }}>
                        {!record.readOnly &&
                          loanTrackingOptions.canEdit &&
                          !loanTrackingDisabled && (
                          <Button
                            variant="outline-primary"
                            className="rounded"
                            size="sm"
                            onClick={() => handleEdit(record)}
                            disabled={isAddingNew || isAddingNewClaim || editingId !== null}
                          >
                            Edit
                          </Button>
                        )}
                        {!record.readOnly &&
                          loanTrackingOptions.canRemove &&
                          !loanTrackingDisabled && (
                          <Button
                            variant="outline-danger"
                            className="rounded"
                            size="sm"
                            onClick={() => handleDelete(record.id)}
                            disabled={isAddingNew || isAddingNewClaim || editingId !== null || isSubmitting}
                            title="Delete this record"
                          >
                            Delete
                          </Button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>

      <ModalWrapper
        show={showPdfModal}
        onHide={handleClosePdfModal}
        onExit={null}
        onExited={null}
        backdropClassName={null}
        centered
        size="lg"
      >
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title className="mb-0">Loan Tracking PDF</Modal.Title>
          <CloseModalButton onCloseModal={handleClosePdfModal} />
        </Modal.Header>
        <Modal.Body className="p-0" style={{ minHeight: 500 }}>
          {pdfPreviewUrl && (
            <embed
              src={pdfPreviewUrl}
              type="application/pdf"
              width="100%"
              height={500}
              style={{ display: 'block', border: 'none' }}
              title="Loan Tracking PDF Preview"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="rounded"
            onClick={handleClosePdfModal}
          >
            Close
          </Button>
          <Button
            variant="primary"
            className="rounded"
            onClick={handleSavePdfToDocuments}
            disabled={loanTrackingDisabled || isSavingPdf}
          >
            {isSavingPdf ? 'Saving…' : 'Save to Documents'}
          </Button>
        </Modal.Footer>
      </ModalWrapper>
    </div>
  );
};

export default ApplicationEventsTab;
